import {
  FileActionSheet,
  getIconBg,
  getIconColor,
} from "@/src/components/ui-kit/attachment-viewer";
import { PostFilePayload } from "@/src/models/post.model";
import { getFileExtension, getFilename } from "@/src/utils/attachment.utils";
import { CloseCircle, Document } from "iconsax-react-nativejs";
import { useState } from "react";
import { Pressable, Text, View } from "react-native";
import { useTheme } from "@/src/styles/theme-provider";

type FileAttachmentProps = {
  fileUrl?: string;
  file?: PostFilePayload;
  onRemove?: () => void;
};

export const FileAttachment = ({
  fileUrl,
  file,
  onRemove,
}: FileAttachmentProps) => {
  const [sheetOpen, setSheetOpen] = useState(false);
  const { themeVars } = useTheme();

  const cleanedUrl = file ? file.uri : (fileUrl ?? "");
  const ext = file
    ? getFileExtension(file.fileName)
    : getFileExtension(cleanedUrl);
  const name = file ? file.fileName : getFilename(cleanedUrl);

  const iconColor = getIconColor(ext);
  const iconBg = getIconBg(ext);

  return (
    <>
      <Pressable
        onPress={() => !onRemove && setSheetOpen(true)}
        className="flex-row items-center min-w-[150px] max-w-[200px] border border-ash-3 rounded-xl px-3 py-2 bg-chalk gap-3 hover:bg-ash-3"
      >
        {/* Icon Container */}
        <View
          style={{ backgroundColor: iconBg }}
          className="size-10 rounded-xl items-center justify-center relative"
        >
          <Document size={22} color={themeVars[iconColor]} variant="Bold" />

          {/* Small extension badge in the corner */}
          {ext && (
            <View
              className="absolute -bottom-0.5 -right-0.5 bg-white rounded-md px-1 py-0.5 border border-white/80"
              style={{ backgroundColor: iconBg }}
            >
              <Text
                style={{ color: iconColor }}
                className="text-[9px] font-bold uppercase tracking-wider"
              >
                {ext}
              </Text>
            </View>
          )}
        </View>

        {/* File Name */}
        <View className="flex-1 min-w-0">
          <Text numberOfLines={1} className="text-onyx text-xs font-medium">
            {name}
          </Text>
        </View>

        {/* Remove Button */}
        {onRemove && (
          <Pressable
            onPress={onRemove}
            hitSlop={8}
            className="rounded-full hover:bg-ash-3"
          >
            <CloseCircle
              size={18}
              color={themeVars["--color-slate-default"] as string}
            />
          </Pressable>
        )}
      </Pressable>

      <FileActionSheet
        attachment={{ url: cleanedUrl, type: "file" }}
        visible={sheetOpen}
        onClose={() => setSheetOpen(false)}
      />
    </>
  );
};
